import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as _ from 'underscore';

import { Messages } from '../../../codegen/Localize';
import { angularProviderHOC } from '../../../common/react/AngularProviderHOC';
import { UserSettingsActions } from '../../../ts/actions/UserSettingsActions';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { ResultHelper } from '../../../ts/helpers/ResultHelper';
import { IServerUserInfo } from '../../../ts/models/IServerUserInfo';
import { ServerUsers } from '../../../ts/resources/ServerUsers';
import { SiteRoleOptionsResource } from '../../../ts/resources/SiteRoleOptions';
import { IGetUserSettingsResult } from '../../../ts/ServerApi/requests/GetUserSettingsRequest';
import { Telemetry } from '../../../ts/services/TelemetryService';
import { updateBreadcrumbs } from '../../actions/UpdateBreadcrumbsAction';
import { BreadcrumbStateInfo } from '../../content/breadcrumbs/BreadcrumbStateInfo';
import { SessionUserSelectors } from '../../selectors/SessionInfoSelectors';
import { ServerUrls } from '../../ServerUrls';
import { ServiceLocator } from '../../ServiceLocator';
import { SpaceInfo } from '../../state/SpaceInfo';
import { MapDispatchToProps, MapStateToProps } from '../../utils/ConnectUtils';
import { RouterError } from '../RouterHelpers/RouterError';
import { ConnectedSpaceInfoProvider } from '../RouterHelpers/SpaceInfoProvider';
import { ServerUserSpace, ServerUserSpaceProps } from './ServerUserSpace';

export function getServerUsersPlaceBreadcrumb(): BreadcrumbStateInfo {
  const serverUrls = ServerUrls.getInstance();
  return {
    id: serverUrls.usersUrl(),
    text: Messages.allServerUsers(),
    href: serverUrls.usersUrl(),
    testId: 'server-users-link'
  };
}

export interface ServerUserSpaceProviderOwnProps extends ReactDirectiveProps {
  readonly userName: string;
  readonly domainName: string;
  readonly renderSpace: (props: ServerUserSpaceProps) => JSX.Element;
}

export interface ServerUserSpaceProviderStateProps {
  readonly isSessionUserSelf: boolean;
}

export interface ServerUserSpaceProviderDispatchProps {
  readonly updateBreadcrumbs: (breadcrumbs: ReadonlyArray<BreadcrumbStateInfo>) => void;
}

interface ServerUserSpaceProverPropsFromAngular {
  readonly serverUsers: ServerUsers;
  readonly telemetry: Telemetry;
  readonly userSettingsActions: UserSettingsActions;
  readonly siteRoleOptions: SiteRoleOptionsResource;
}

export type ServerUserSpaceNonAngularProps = ServerUserSpaceProviderOwnProps & ServerUserSpaceProviderStateProps & ServerUserSpaceProviderDispatchProps;

export type ServerUserSpaceProviderComponentProps = ServerUserSpaceNonAngularProps & ServerUserSpaceProverPropsFromAngular;

export interface ServerUserSpaceProviderState {
  readonly initialRender: boolean;
  readonly renderChildren: boolean;
  readonly hasError: boolean;
  readonly serverUser: IServerUserInfo;
  readonly userSettings: IGetUserSettingsResult;
  readonly userRefreshTokenCount: number;
}

export class ServerUserSpaceProviderComponent extends React.PureComponent<ServerUserSpaceProviderComponentProps, ServerUserSpaceProviderState> {
  private unmounted: boolean = false;
  private readonly initialState: ServerUserSpaceProviderState = {
    initialRender: true,
    renderChildren: false,
    hasError: false,
    serverUser: undefined,
    userSettings: undefined,
    userRefreshTokenCount: undefined
  };

  public constructor(props: ServerUserSpaceProviderComponentProps) {
    super(props);
    this.state = this.initialState;
  }

  public render() {
    if (this.state.renderChildren) {
      return this.props.renderSpace({
        angularContext: this.props.angularContext,
        user: this.state.serverUser,
        userSettings: this.state.userSettings,
        userRefreshTokenCount: this.state.userRefreshTokenCount,
        siteRoleOptions: this.props.siteRoleOptions,
        isSessionUserSelf: this.props.isSessionUserSelf,
      });
    } else if (this.state.hasError) {
      return <RouterError errorType={'serveruser'} urls={undefined} />;
    } else {
      return null;
    }
  }

  public componentDidMount() {
    this.getData();
  }

  public componentDidUpdate(prevProps: ServerUserSpaceProviderComponentProps) {
    if (this.userChanged(prevProps)) {
      this.updateState(this.initialState);
      this.getData();
      return;
    }

    // if we dont have all we need - do nothing
    if (!this.haveAllDataNeededToRender()) {
      return;
    }

    if (this.state.initialRender) {
      this.updateBreadcrumbs();
      this.setState({initialRender: false, renderChildren: true});
    }
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  private updateBreadcrumbs() {
    const breadcrumbs: BreadcrumbStateInfo[] =  [
      getServerUsersPlaceBreadcrumb(),
      { text: this.state.serverUser.displayName }
    ];

    this.props.updateBreadcrumbs(breadcrumbs);
  }

  private haveAllDataNeededToRender() {
    return !_.isUndefined(this.state.serverUser) && !_.isUndefined(this.state.userSettings)
      && !_.isUndefined(this.state.userRefreshTokenCount);
  }

  private userChanged(prevProps: ServerUserSpaceProviderComponentProps): boolean {
    return (!_.isUndefined(prevProps.userName)
      && !_.isUndefined(this.props.userName)
      && prevProps.userName !== this.props.userName) ||
      (!_.isUndefined(prevProps.domainName)
      && !_.isUndefined(this.props.domainName)
      && prevProps.domainName !== this.props.domainName);
  }

  private getData = async () => {
    this.getUser();
    this.getUserSettings();
    this.getUserRefreshTokenCount();
  }

  private updateState<K extends keyof ServerUserSpaceProviderState>(newState: Pick<ServerUserSpaceProviderState, K>, callback?: () => void): void {
    if (!this.unmounted) {
      this.setState(newState, callback);
    }
  }

  private getUser = async () => {
    try {
      const serverUser = await this.props.serverUsers.getUserByUsernameAndDomain(this.props.userName, this.props.domainName);
      if (serverUser) {
        this.updateState({serverUser});
      } else {
        this.updateState({hasError: true});
      }
    } catch (e) {
      this.updateState({hasError: true});
    }
  }

  private getUserRefreshTokenCount = async () => {
    try {
      const userRefreshTokenCount = await this.props.serverUsers.getUserRefreshTokenCount(this.props.userName, this.props.domainName);
      this.updateState({userRefreshTokenCount});
    } catch (e) {
      this.updateState({hasError: true});
    }
  }

  private getUserSettings = async () => {
    try {
      const userSettings = await this.props.userSettingsActions.fetchUserSettings(this.props.userName, this.props.domainName);
      if (ResultHelper.hasErrors(userSettings)) {
        this.updateState({hasError: true});
      } else {
        this.updateState({userSettings});
      }
    } catch (e) {
      this.updateState({hasError: true});
    }
  }
}

export namespace ServerUserSpaceProviderAngularProvider {
  function getAngularServicesWithServiceLocator() {
    return {
      serverUsers: ServiceLocator.get<ServerUsers>('ServerUsers'),
      telemetry: ServiceLocator.get<Telemetry>('TelemetryService'),
      userSettingsActions: ServiceLocator.get<UserSettingsActions>('UserSettingsActions'),
      siteRoleOptions: ServiceLocator.get<SiteRoleOptionsResource>('SiteRoleOptionsResource'),
    };
  }

  function selectPropsFromAngularServices(angularServices: ServerUserSpaceProverPropsFromAngular) {
    return angularServices;
  }

  export const HOC = angularProviderHOC<ServerUserSpaceNonAngularProps, ServerUserSpaceProverPropsFromAngular, ServerUserSpaceProverPropsFromAngular>({
    getAngularServicesWithServiceLocator,
    selectPropsFromAngularServices,
  });
}
const ServerUserSpaceProviderAng: React.ComponentType<ServerUserSpaceNonAngularProps> = ServerUserSpaceProviderAngularProvider.HOC(ServerUserSpaceProviderComponent);

const mapStateToProps: MapStateToProps<ServerUserSpaceProviderStateProps, ServerUserSpaceProviderOwnProps> = (appState, ownProps) => {
  return {
    isSessionUserSelf: SessionUserSelectors.sessionUserName(appState) === ownProps.userName && SessionUserSelectors.sessionDomainName(appState) === ownProps.domainName,
  };
};

const mapDispatchToProps: MapDispatchToProps<ServerUserSpaceProviderDispatchProps> = (dispatch) => bindActionCreators({
  updateBreadcrumbs
}, dispatch);

export const ConnectedServerUserSpaceProviderComponent: React.ComponentClass<ServerUserSpaceProviderOwnProps>
  = connect(mapStateToProps, mapDispatchToProps)(ServerUserSpaceProviderAng);

const renderSpace = (props: ServerUserSpaceProps): JSX.Element => <ServerUserSpace {...props} />;

export type ServerUserSpaceProviderProps = Omit<ServerUserSpaceProviderOwnProps, 'renderSpace'>;

export function ServerUserSpaceProvider(props: ServerUserSpaceProviderProps) {
  const spaceInfo: SpaceInfo = {
    item: null,
    spaceType: 'serveruser',
    subspaceName: undefined
  };

  const componentProps: ServerUserSpaceProviderOwnProps = {
    domainName: props.domainName,
    userName: props.userName,
    angularContext: props.angularContext,
    renderSpace
  };

  return (
    <ConnectedSpaceInfoProvider spaceInfo={spaceInfo}>
      <ConnectedServerUserSpaceProviderComponent {...componentProps}/>
    </ConnectedSpaceInfoProvider>
  );
}
